import {
  attachedCardMessage,
  finalDismissalOf,
  getInformationalCardKind,
  type ActionCardSurface,
  type DeferredDismissState,
} from '@littlebird/action-cards';
import {
  isCardChanged,
  isRefusal,
  type CardFieldsCommit,
} from '@littlebird/action-cards-view/react';

import {
  actionCardAnalyticsContext,
  actionCardAnalyticsSnapshot,
  trackCardCommandFailed,
} from '@/features/action-cards/analytics';
import { actionCardCommands } from '@/features/action-cards/commands';
import { useTabs } from '@/features/tabs';
import { logError } from '@/lib/errorLogging';

import {
  reportedSurfaceOf,
  trackCardChatClicked,
  trackCardAcknowledgedSnapshot,
  actionCardEventSnapshot,
  trackCardCompletedSnapshot,
  trackCardEditSavedSnapshot,
  trackCardRun,
  trackCardSourceOpened,
  type WebSurface,
} from './analytics';
import {
  cardChatSeed,
  commitCardFields,
  openCardChatTab,
  openCardLink,
  restoreCard,
  runCard,
  startCardChat,
  startChatFromCard,
} from './commands';

import type {
  CardActionHandlers,
  CardDoneVia,
} from '@littlebird/action-cards-view';
import type { ActionCard } from '@littlebird/sdk';

type CardHandlersOptions = {
  deferredDismiss: DeferredDismissState;
  reportedSurface?: ActionCardSurface;
  openCardChat?: (seed?: string) => void;
  // Where a plain Chat goes on a host without the in-place chat; a take-over still starts its own chat
  openPlainCardChat?: () => void;
  cardExpanded?: boolean;
  // Told the revision each saved edit produced
  onEditSaved?: (savedSyncId: number) => void;
};

const CARD_CHAT_MODE = 'card';

// Handlers read the card when pressed, so a card updated in place is never stale here
export function useCardHandlers(
  card: ActionCard,
  surface: WebSurface,
  {
    deferredDismiss,
    reportedSurface = reportedSurfaceOf(surface),
    openCardChat,
    openPlainCardChat,
    cardExpanded = false,
    onEditSaved,
  }: CardHandlersOptions
): {
  handlers: CardActionHandlers;
  onFieldsCommit: CardFieldsCommit;
  completeElsewhere: (via: CardDoneVia) => Promise<void>;
} {
  const { openTab } = useTabs();

  const handlers: CardActionHandlers = {
    // `seenSyncId` comes from the card's session. A press from outside it, as a collapsed feed row's or a shortcut's,
    // names the version the store holds, which is the one the row draws
    run: (key, seenSyncId) => {
      const context = actionCardAnalyticsContext(reportedSurface, {
        card_expanded: cardExpanded,
      });
      const analytics = actionCardEventSnapshot(card, context);
      trackCardRun(card, reportedSurface, context);
      return runCard(
        card,
        key,
        seenSyncId ?? card.syncId,
        reportedSurface,
        context,
        analytics
      );
    },
    dismiss: (dismissal) => {
      deferredDismiss.beginDismiss(
        [
          {
            cardId: card.id,
            actionType: card.actionType,
            cardKind: getInformationalCardKind(card),
            origin: card.origin,
            surface: reportedSurface,
            analytics: actionCardAnalyticsSnapshot(card, reportedSurface, {
              card_expanded: cardExpanded,
            }),
          },
        ],
        dismissal
      );
    },
    acknowledge: async (via = 'card_button') => {
      const context = actionCardAnalyticsContext(reportedSurface, {
        card_expanded: cardExpanded,
      });
      const analytics = {
        ...actionCardEventSnapshot(card, context),
        done_via: via,
      };
      const outcome = await actionCardCommands.acknowledge(card.id);
      if (outcome.status === 'succeeded') {
        trackCardAcknowledgedSnapshot(analytics);
        trackCardCompletedSnapshot(analytics, 'acknowledge', card.status);
      } else if (outcome.status === 'failed' && !outcome.silent) {
        // `skipped` means another write to the card was in flight, such as the first of two presses
        trackCardCommandFailed(analytics, 'acknowledge');
      }
    },
    restore: finalDismissalOf(card)
      ? undefined
      : () => {
          // A card dismissed moments ago is still behind its Undo, which brings it back once the dismissal is written
          const strip = deferredDismiss.stripForCard(card.id);
          if (strip?.undoable) {
            deferredDismiss.undo(strip.id);
            return Promise.resolve();
          }
          const context = actionCardAnalyticsContext(reportedSurface, {
            card_expanded: cardExpanded,
          });
          return restoreCard(card, reportedSurface, context);
        },
    open: (url: string, saveEdits?: () => Promise<boolean>) => {
      const context = actionCardAnalyticsContext(reportedSurface, {
        card_expanded: cardExpanded,
      });
      trackCardSourceOpened(card, reportedSurface, context);
      openCardLink(
        card,
        url,
        actionCardEventSnapshot(card, context),
        saveEdits
      );
    },
    chat: (params) => {
      const context = actionCardAnalyticsContext(reportedSurface, {
        card_expanded: cardExpanded,
      });
      trackCardChatClicked(
        card,
        reportedSurface,
        params.attach && params.prompt.trim()
          ? 'cta'
          : params.ask
            ? 'take_over'
            : params.mode === CARD_CHAT_MODE || !params.prompt.trim()
              ? 'open'
              : 'prompt',
        context
      );
      const analytics = actionCardEventSnapshot(card, context);
      const onStartFailed = (error: unknown) => {
        trackCardCommandFailed(analytics, 'start_chat');
        logError(error, 'Failed to start chat from action card', {
          context: 'CardV2:chat',
          metadata: { cardId: card.id },
        });
      };
      if (params.attach && params.prompt.trim()) {
        const prompt = attachedCardMessage(card, params.prompt);
        // The Feed docks the card's chat beside it, as its take-over does; elsewhere the prompt starts a new chat tab
        if (openCardChat) {
          // A chat already started from the card only reopens, so its prompt is never sent twice
          openCardChat(card.followUpChatId ? undefined : prompt);
          return;
        }
        void startCardChat(card, openTab, prompt, context).catch(onStartFailed);
        return;
      }
      // An empty prompt has nothing to send, so it also opens a chat about this card
      const isCardChat =
        params.mode === CARD_CHAT_MODE || !params.prompt.trim();
      // Take-over asks the agent; Chat opens the card's chat for the user to type
      const seed = isCardChat && params.ask ? cardChatSeed(card) : undefined;
      if (isCardChat && openCardChat) {
        openCardChat(seed);
        return;
      }
      if (isCardChat && !seed) {
        openCardChatTab(card, openTab, openPlainCardChat);
        return;
      }
      const started = isCardChat
        ? startCardChat(card, openTab, undefined, context)
        : startChatFromCard(card, params, {
            openTab,
            surface: reportedSurface,
            context,
          });
      void started.catch(onStartFailed);
    },
  };

  const onFieldsCommit: CardFieldsCommit = async (fields) => {
    const analytics = actionCardEventSnapshot(
      card,
      actionCardAnalyticsContext(reportedSurface, {
        card_expanded: cardExpanded,
      })
    );
    const outcome = await commitCardFields(
      card,
      fields,
      onEditSaved,
      analytics
    );
    if (!isRefusal(outcome) && !isCardChanged(outcome)) {
      trackCardEditSavedSnapshot(analytics);
    }
    return outcome;
  };

  // Marks a draft done without running it, as when the user already handled it elsewhere. The server keeps it as
  // completed elsewhere, not sent
  const completeElsewhere = async (via: CardDoneVia) => {
    const context = actionCardAnalyticsContext(reportedSurface, {
      card_expanded: cardExpanded,
    });
    const analytics = {
      ...actionCardEventSnapshot(card, context),
      done_via: via,
    };
    const outcome = await actionCardCommands.completeExternally(card.id);
    if (outcome.status === 'succeeded')
      trackCardCompletedSnapshot(analytics, 'marked_done', card.status);
    else if (outcome.status === 'failed' && !outcome.silent)
      trackCardCommandFailed(analytics, 'complete_externally');
  };

  return { handlers, onFieldsCommit, completeElsewhere };
}
